import {WMeasurementDimension, WMeasurementRange, WMeasurementUnits} from "./types";
import {unitsi18n} from "./i18n/units";

export type WComplexMeasurementUnits = {
///    type: "complex";
    numerator: WMeasurementUnits[];
    denominator?: WMeasurementUnits[];
}
///export type WSimpleUnits = {
///    type: "simple";
///    value: WMeasurementUnits;
//}
export type WDimensionAndScale = {
    dimension: WMeasurementDimension;
    scale: number;
    offset: number;
    dimensionWithOffset: boolean;
}

///export type WFullUnits = WComplexMeasurementUnits | WSimpleUnits;

export const unitToSynonyms = (unit: WMeasurementUnits):string=> unitsListArr.find(ul => ul.mUnit === unit)?.synonym;

export const unitToString = (mu: string, isSingle:boolean, lang:string) => {
    const unit = unitsListArr.find(ul => ul.mUnit === mu);
    const str = unit?.symbol || unit?.synonym;
    if (lang == 'en' && unit.dimension == WMeasurementDimension.Time && !isSingle) {
        //if doesn't end with 's' adds 's'
        if (str[str.length - 1] != 's') {
            return str + 's';
        }
    }
    return str;

};

const MULTIPLIER_SIGN = " x ";

export const complexUnitsToString = (unit: WComplexMeasurementUnits, isSingle?:boolean, separator?: string, lang: string = "en"): string => {

    if (!separator && separator !== "") separator = "/";


    const n = unit.numerator.map(n => unitsi18n?.[lang]?.[n]?.[isSingle ? "single": "multiple"] || unitToString(n,isSingle,lang)).join(MULTIPLIER_SIGN);
    const d = maybeNull(unit.denominator).map(d => unitsi18n?.[lang]?.[d]?.single || unitToString(d,true,lang)).join(MULTIPLIER_SIGN);

    return n.trim() + ((d.trim() != "") ? separator : "") + d.trim();
}

export type WUnitsInfo = {
    mUnit: WMeasurementUnits;
    dimension: WMeasurementDimension;
    scale: number;
    offset?: number,
    synonym: string;
    matchExp: RegExp;
    symbol?: string;
}

type TranslationOptions = {
    complexUnitsSeparator?: string
    allowNullEdges?: boolean
    belowString?: string;
    moreThanString?: string;
}

export function translateUnits(lang: string = "en") : (range: WMeasurementRange, options?: TranslationOptions ) => string{
    return function ({units, low, high, complexUnits} : WMeasurementRange, options: TranslationOptions = {}): string {
        if (!(options?.allowNullEdges)) {
            if (!low && high) low = high;
            if (low && !high) high = low;
        }

        let isSingle = low === high && low == 1;
        const unitsStr = complexUnits ? complexUnitsToString(complexUnits, isSingle, options.complexUnitsSeparator, lang)
            : lang == "en" ? `${units}`
                : unitToString(units,isSingle,lang);

        if (lang === "en") {
            if (low === high) return `${low} ${unitsStr}`.toLowerCase();
            else if (!low) {
                return `${options.belowString || `less than `}${high} ${unitsStr}`.toLowerCase();
            }
            else if (!high) {
                return `${options.moreThanString || `more than `}${low} ${unitsStr}`.toLowerCase();
            }
            return `${low}-${high} ${unitsStr}`.toLowerCase();
        } else {
            if (low === high) {
                if (low === 1) {
                    return `${complexUnits ? `${low} `: ``}${unitsi18n?.[lang]?.[units]?.single || unitsStr}`.toLowerCase()
                } else {
                    return `${low} ${unitsi18n?.[lang]?.[units]?.multiple || unitsStr}`.toLowerCase()
                }
            } else {
                return `${low}-${high} ${unitsi18n?.[lang]?.[units]?.multiple || unitsStr}`.toLowerCase()
            }
        }
    };
}

export const unitsListArr: WUnitsInfo[] = [
    // length
    { mUnit:WMeasurementUnits.Millimeters, dimension: WMeasurementDimension.Length, scale: 0.001, synonym: "mm", matchExp: /^\s*mm\s*$/i},
    { mUnit:WMeasurementUnits.Centimeters, dimension: WMeasurementDimension.Length, scale: 0.01, synonym: "cm", matchExp: /^\s*cm\s*$/i},
    { mUnit:WMeasurementUnits.Meters, dimension: WMeasurementDimension.Length, scale: 1, synonym: "m", matchExp: /^\s*m\s*$/i},
    { mUnit:WMeasurementUnits.Inches, dimension: WMeasurementDimension.Length, scale: 0.0254, synonym: "\"", matchExp: /^\s*\"\s*$/i},
    { mUnit:WMeasurementUnits.Feet, dimension: WMeasurementDimension.Length, scale: 0.3048, synonym: "ft", matchExp: /^\s*ft\s*$/i},

    // area
    { mUnit:WMeasurementUnits.MeterSquare, dimension: WMeasurementDimension.Area, scale: 1, synonym: "m2", matchExp: /^\s*m2\s*$/i},

    // volume
    { mUnit:WMeasurementUnits.Liter, dimension: WMeasurementDimension.Volume, scale: 1, synonym: "L", matchExp: /^\s*l\s*$/i},
    { mUnit:WMeasurementUnits.Deciliter, dimension: WMeasurementDimension.Volume, scale: 0.1, synonym: "dL", matchExp: /^\s*dl\s*$/i},
    { mUnit:WMeasurementUnits.Milliliter, dimension: WMeasurementDimension.Volume, scale: 0.001, synonym: "ml", matchExp: /^\s*ml\s*$/i},
    { mUnit:WMeasurementUnits.Microliter, dimension: WMeasurementDimension.Volume, scale: 0.000001, synonym: "mcl", matchExp: /^\s*mcl\s*$/i},
    { mUnit:WMeasurementUnits.Femtoliter, dimension: WMeasurementDimension.Volume, scale: 0.000000000000001, synonym: "fL", matchExp: /^\s*fl\s*$/i},

    // weight
    { mUnit:WMeasurementUnits.Grams, dimension: WMeasurementDimension.Weight, scale: 1, synonym: "g", matchExp: /^\s*g\s*$/i},
    { mUnit:WMeasurementUnits.Milligrams, dimension: WMeasurementDimension.Weight, scale: 0.001, synonym: "mg", matchExp: /^\s*mg\s*$/i},
    { mUnit:WMeasurementUnits.Micrograms, dimension: WMeasurementDimension.Weight, scale: 0.000001, synonym: "mcg", matchExp: /^\s*(mc|u)g\s*$/i},
    { mUnit:WMeasurementUnits.Nanograms, dimension: WMeasurementDimension.Weight, scale: 0.000000001, synonym: "ng", matchExp: /^\s*ng\s*$/i},
    { mUnit:WMeasurementUnits.Picograms, dimension: WMeasurementDimension.Weight, scale: 0.000000000001, synonym: "pg", matchExp: /^\s*pg\s*$/i},
    { mUnit:WMeasurementUnits.Kilograms, dimension: WMeasurementDimension.Weight, scale: 1000, synonym: "kg", matchExp: /^\s*kg\s*$/i},
    { mUnit:WMeasurementUnits.Pounds, dimension: WMeasurementDimension.Weight, scale: 453.592, synonym: "lb", matchExp: /^\s*lb\s*$/i},

    // time
    { mUnit:WMeasurementUnits.Seconds, dimension: WMeasurementDimension.Time, scale: 1 / (365 * 24 * 60 * 60), synonym: "sec", matchExp: /^\s*s(ec|econd|econds)?\s*$/i},
    { mUnit:WMeasurementUnits.Minutes, dimension: WMeasurementDimension.Time, scale: 1 / (365 * 24 * 60), synonym: "min", matchExp: /^\s*m(in|inute|inutes)?\s*$/i},
    { mUnit:WMeasurementUnits.Hours, dimension: WMeasurementDimension.Time, scale: 1 / (365 * 24), synonym: "hour", matchExp: /^\s*h(our|ours)?\s*$/i},
    { mUnit:WMeasurementUnits.Days, dimension: WMeasurementDimension.Time, scale: 1 / (365), synonym: "day", matchExp: /^\s*(d(ay|ays)?|24\s*h)\s*$/i},
    { mUnit:WMeasurementUnits.Weeks, dimension: WMeasurementDimension.Time, scale: 7 / (365), synonym: "week", matchExp: /^\s*w(eek|eeks)?\s*$/i},
    { mUnit:WMeasurementUnits.Months, dimension: WMeasurementDimension.Time, scale: 30 / (365), synonym: "month", matchExp: /^\s*m(onth|onths)?\s*$/i},
    { mUnit:WMeasurementUnits.Years, dimension: WMeasurementDimension.Time, scale: 1, synonym: "year", matchExp: /^\s*y(ear|ears)?\s*$/i},

    // pure units
    { mUnit:WMeasurementUnits.NA, dimension: WMeasurementDimension.Pure, scale: 1, synonym: "", matchExp: /^$/i},
    { mUnit:WMeasurementUnits.Units, dimension: WMeasurementDimension.Unit, scale: 1, synonym: "U", matchExp: /^\s*(e|i)?u(nits)?\s*$/i},
    { mUnit:WMeasurementUnits.MilliUnits, dimension: WMeasurementDimension.Unit, scale: 0.001, synonym: "mU", matchExp: /^\s*m(e|i)?u\s*$/i},
    { mUnit:WMeasurementUnits.MicroUnits, dimension: WMeasurementDimension.Unit, scale: 0.000001, synonym: "mcU", matchExp: /^\s*mc(e|i)?u\s*$/i},
    { mUnit:WMeasurementUnits.Milliequivalent, dimension: WMeasurementDimension.Unit, scale: 0.001, synonym: "mEq", matchExp: /^\s*meq\s*$/i},
    { mUnit:WMeasurementUnits.KiloUnits, dimension: WMeasurementDimension.Unit, scale: 1000, synonym: "10^3", matchExp: /^\s*10\^?3(\s*cells)?\s*$/i},
    { mUnit:WMeasurementUnits.MegaUnits, dimension: WMeasurementDimension.Unit, scale: 1000000, synonym: "10^6", matchExp: /^\s*10\^?6(\s*cells)?\s*$/i},
    { mUnit:WMeasurementUnits.GigaUnits, dimension: WMeasurementDimension.Unit, scale: 1000000000, synonym: "10^9", matchExp: /^\s*10\^?9(\s*cells)?\s*$/i},

    { mUnit:WMeasurementUnits.Mole, dimension: WMeasurementDimension.Mole, scale: 1, synonym: "mol", matchExp: /^\s*mol\s*$/i},
    { mUnit:WMeasurementUnits.MilliMole, dimension: WMeasurementDimension.Mole, scale: 0.001, synonym: "mMol", matchExp: /^\s*mmol\s*$/i},
    { mUnit:WMeasurementUnits.MilliOsmolality, dimension: WMeasurementDimension.Mole, scale: 0.001, synonym: "mOsm", matchExp: /^\s*mosm\s*$/i},
    { mUnit:WMeasurementUnits.NanoMole, dimension: WMeasurementDimension.Mole, scale: 0.000000001, synonym: "nmol", matchExp: /^\s*nmol\s*$/i},
    { mUnit:WMeasurementUnits.PicoMole, dimension: WMeasurementDimension.Mole, scale: 0.000000000001, synonym: "pmol", matchExp: /^\s*pmol\s*$/i},

    { mUnit:WMeasurementUnits.Percent, dimension: WMeasurementDimension.Pure, scale: 1, synonym: "%", matchExp: /^\s*%\s*$/i},

    // pressure
    { mUnit:WMeasurementUnits.MmHg, dimension: WMeasurementDimension.Pressure, scale: 1, synonym: "mm Hg", matchExp: /^\s*mm\s*hg\s*$/i},

    // Temperature
    { mUnit:WMeasurementUnits.Celsius, dimension: WMeasurementDimension.Temperature, scale: 1, synonym: "Celsius", symbol: "\u2103", matchExp: /^\s*(Celsius|\u{2103})\s*$/iu},
    { mUnit:WMeasurementUnits.Fahrenheit, dimension: WMeasurementDimension.Temperature, scale: 5/9, offset: 32, synonym: "Fahrenheit",  symbol: "\u2109", matchExp: /^\s*(Fahrenheit|\u{2109})\s*$/iu},

    //frequency
    { mUnit:WMeasurementUnits.Times, dimension: WMeasurementDimension.Frequency, scale: 1, synonym: "Times per", matchExp: /^\s*(times|Times|Per)\s*$/i},

    // Packs
    { mUnit:WMeasurementUnits.Pack, dimension: WMeasurementDimension.Special, scale: 1, synonym: "Pack", symbol: "pack", matchExp: /^\s*pack\s*$/i},

    // special
    { mUnit:WMeasurementUnits.KgBodyWeight, dimension: WMeasurementDimension.Special, scale: 1, synonym: "Kg body weight", matchExp: /^\s*kg\s*body\s*w(eigh)?t\s*$/i},
    { mUnit:WMeasurementUnits.PercentOfNormal, dimension: WMeasurementDimension.Special, scale: 1, synonym: "% of normal", matchExp: /^\s*%\s*of\s*norm(al)?\s*$/i},
    { mUnit:WMeasurementUnits.GramHemoglobin, dimension: WMeasurementDimension.Special, scale: 1, synonym: "g hemoglobin", matchExp: /^\s*g\s*hg?b\s*$/i},
    { mUnit:WMeasurementUnits.GramCreatinine, dimension: WMeasurementDimension.Special, scale: 1, synonym: "g creatinine", matchExp: /^\s*g\s*creatinine\s*$/i},
    { mUnit:WMeasurementUnits.KilogramH2O, dimension: WMeasurementDimension.Special, scale: 1, synonym: "Kg H2O", matchExp: /^\s*kg\s+H2O\s*$/i},
    { mUnit:WMeasurementUnits.Priority, dimension: WMeasurementDimension.Special, scale: 1, synonym: "Priority", matchExp: /^\s*Priority\s*$/i},
    { mUnit:WMeasurementUnits.BeatsPerMinute, dimension: WMeasurementDimension.Frequency, scale: 1, synonym: "BPM", matchExp: /^\s*BPM\s*$/i},
    { mUnit:WMeasurementUnits.RespirationsPerMinute, dimension: WMeasurementDimension.Frequency, scale: 1, synonym: "RPM", matchExp: /^\s*RPM\s*$/i},
];

const spreadDimensionOffset = (listMap: Map<WMeasurementUnits, WDimensionAndScale>): Map<WMeasurementUnits, WDimensionAndScale> => {
    const dimensionsWithOffset = Array.from((new Set(Array.from(listMap.values()).filter(v => v.offset != undefined).map(v => v.dimension))));
    dimensionsWithOffset.forEach(dim => {
        Array.from(listMap.keys()).forEach(k => {
           if (listMap.get(k).dimension === dim) {
               listMap.set(k, {...listMap.get(k), dimensionWithOffset: true})
           }
        });
    })
    return listMap;
}
const unitsListMap: Map<WMeasurementUnits, WDimensionAndScale> = spreadDimensionOffset(unitsListArr.reduce((map, obj) => {
    map.set(obj.mUnit, { dimension: obj.dimension, scale: obj.scale, offset: obj.offset, dimensionWithOffset: false });
    return map;
}, new Map<WMeasurementUnits, WDimensionAndScale>()));

function getDimensionAndScale(unit: WMeasurementUnits) {
    const ds = unitsListMap.get(unit)
    if (!ds) console.log("Invalid WMeasurementUnit", unit);
    return ds || unitsListMap.get(WMeasurementUnits.NA);
}
//console.log(unitsListMap);

const canonicalUnitMap: Map<WMeasurementDimension, WMeasurementUnits> = new Map(Object.values(WMeasurementDimension).map(dim=>{
    // reuse existing canonical unit
    const existingUnit = unitsListArr.find(({dimension, scale, offset})=>dimension == dim && scale == 1 && !offset);
    if (existingUnit) return [dim, existingUnit.mUnit];
    // else add a new canonical unit for this dimension
    const newUnit = `Canonical-${dim}` as WMeasurementUnits;
    unitsListMap.set(newUnit, {dimension: dim, scale: 1, offset: 0, dimensionWithOffset: false})
    return [dim, newUnit];
}));

export function getCanonicalOfSimpleUnit(unit: WMeasurementUnits) {
    const dim = getDimensionAndScale(unit)?.dimension;
    if (!dim) console.error("Invalid WMeasurementUnit", unit);
    return dim ? canonicalUnitMap.get(dim) : WMeasurementUnits.NA;
}
export function getCanonicalOfComplexUnit(cunit: WComplexMeasurementUnits) {
    return {
        numerator: cunit.numerator.map(getCanonicalOfSimpleUnit),
        denominator: maybeNull(cunit.denominator).map(getCanonicalOfSimpleUnit)
    }
}


//console.log("Canonical Units", (canonicalUnitMap));


// translate simple and complex into complex units
///export function toComplexUnits(unit: WFullUnits): WComplexMeasurementUnits {
///    switch (unit.type) {
///        case "complex": return unit;
///        case "simple": return { type: "complex", numerator: [unit.value], denominator: [] }
///    }
///    return null;
///}

function sameDimensionArray(arr1: WDimensionAndScale[], arr2: WDimensionAndScale[]): boolean {
    return arr1.every(ds =>
        arr1.filter(ds1 => ds1.dimension === ds.dimension).length ===
        arr2.filter(ds2 => ds2.dimension === ds.dimension).length);
}

function scaleDimensionArray(arr: WDimensionAndScale[]): number {
    return arr.reduce((acc, d) => acc * d.scale, 1.0);
}

function isSameDimension(translatedUnit: WComplexMeasurementUnits, givenUnits: WComplexMeasurementUnits): { isSame: boolean, scaler: number } {
    if (length(translatedUnit.numerator) !== length(givenUnits.numerator) ||
        length(translatedUnit.denominator) !== length(givenUnits.denominator)) {
        return {isSame: false, scaler: 1};
    }
    const num1 = translatedUnit.numerator.map(getDimensionAndScale);
    const num2 = givenUnits.numerator.map(getDimensionAndScale);
    const denom1 = maybeNull(translatedUnit.denominator).map(getDimensionAndScale);
    const denom2 = maybeNull(givenUnits.denominator).map(getDimensionAndScale);

    const isSame = sameDimensionArray(num1, num2) && sameDimensionArray(denom1, denom2);
    if (!isSame) return {isSame: false, scaler: 1};
    return {
        isSame: true,
        scaler: scaleDimensionArray(num2) / scaleDimensionArray(denom2) * scaleDimensionArray(denom1) / scaleDimensionArray(num1)
    }
}

export function validateMeasurementUnit(unit: WComplexMeasurementUnits): boolean {
    if (unit.numerator.length > 0) {
        const withOffset = unit.numerator.filter(u => getDimensionAndScale(u).dimensionWithOffset).length;
        if (withOffset > 1 || withOffset > 0 && withOffset < unit.numerator.length) {
            return false;
        }
    }
    if (unit.denominator?.length > 0) {
        const withOffset = unit.denominator.filter(u => getDimensionAndScale(u).dimensionWithOffset).length;
        if (withOffset > 1 || withOffset > 0 && withOffset < unit.denominator.length) {
            return false;
        }
    }
    return true;
}

export function translateMeasurement(value: number, givenUnit: WComplexMeasurementUnits, translatedUnit: WComplexMeasurementUnits): number {
    const unitTranslate = isSameDimension(translatedUnit, givenUnit);

    if (! unitTranslate.isSame)
        return undefined;

    const hasOffset =  [...givenUnit.numerator, ...maybeNull(givenUnit.denominator), ...translatedUnit.numerator, ...maybeNull(translatedUnit.denominator)]
      .some(u => getDimensionAndScale(u).offset);

    // if (hasOffset) {
    //     if (length(givenUnit.numerator) != 1 || length(givenUnit.denominator) != 0 || length(translatedUnit.numerator) != 1 || length(translatedUnit.denominator) != 0) {
    //         return undefined;
    //     }
    // }
    const ignoreOffset = hasOffset && (length(givenUnit.numerator) != 1 || length(givenUnit.denominator) != 0 || length(translatedUnit.numerator) != 1 || length(translatedUnit.denominator) != 0);

    const givenUnitrOffset = ignoreOffset? 0: getDimensionAndScale(givenUnit.numerator[0]).offset || 0;
    const translatedUnitrOffset = ignoreOffset? 0: getDimensionAndScale(translatedUnit.numerator[0]).offset || 0;

    return (value - givenUnitrOffset) * unitTranslate.scaler + translatedUnitrOffset;
}

export function translateToCanonicalMeasurement(value: number, givenUnit: WComplexMeasurementUnits)  {
    const complexUnits = getCanonicalOfComplexUnit(givenUnit);
    return {
        value: translateMeasurement(value, givenUnit, complexUnits),
        complexUnits
    };
}

export function toUnitFromName(name: string): WMeasurementUnits {
    if (!name) return WMeasurementUnits.NA;
    const a = unitsListArr.find((ul) => ul.mUnit.toString() === name || name.match(ul.matchExp));
    return (a)? a.mUnit: WMeasurementUnits.NA;
}

export function interpretUnitFromName(name: string): WComplexMeasurementUnits {
    const parts = name.split('/');
    let part0 = parts[0];
    if (part0.startsWith("x "))
        part0 = part0.substring(2);
    const above = part0.split(MULTIPLIER_SIGN).filter(p => p.length > 0);
    const below = parts.slice(1).filter(p => p.length > 0);
    return {
        numerator: above.map(toUnitFromName) || [],
        denominator: below.map(toUnitFromName) || [],
    }
}

export function complexToSimpleUnits(complexUnits: WComplexMeasurementUnits): WMeasurementUnits {
    return length(complexUnits.denominator) == 0 && length(complexUnits.numerator) == 1
        ? complexUnits.numerator[0] : null;
}

export function simpleUnitsToComplex(units: WMeasurementUnits): WComplexMeasurementUnits {
    return {
        numerator: [units]
    }
}

/* /// on older version of getMeasurementDimAndScale
export function getMeasurementDimAndScale(unit: WMeasurementUnits): WDimensionAndScale {

    switch (unit) {

        // length
        case WMeasurementUnits.Centimeters: return {dimension: WMeasurementDimension.Length, scale: 0.01}
        case WMeasurementUnits.Meters:      return {dimension: WMeasurementDimension.Length, scale: 1}

        // area
        case WMeasurementUnits.MeterSquare: return {dimension: WMeasurementDimension.Area, scale: 1}

        // volume
        case WMeasurementUnits.Liter:      return {dimension: WMeasurementDimension.Volume, scale: 1}
        case WMeasurementUnits.Deciliter:  return {dimension: WMeasurementDimension.Volume, scale: 0.1}
        case WMeasurementUnits.Milliliter: return {dimension: WMeasurementDimension.Volume, scale: 0.001}
        case WMeasurementUnits.Femtoliter: return {dimension: WMeasurementDimension.Volume, scale: 0.000000000000001}
        // weight
        case WMeasurementUnits.Grams:      return {dimension: WMeasurementDimension.Weight, scale: 1}
        case WMeasurementUnits.Milligrams: return {dimension: WMeasurementDimension.Weight, scale: 0.001}
        case WMeasurementUnits.Micrograms: return {dimension: WMeasurementDimension.Weight, scale: 0.000001}
        case WMeasurementUnits.Nanograms:  return {dimension: WMeasurementDimension.Weight, scale: 0.000000001}
        case WMeasurementUnits.Picograms:  return {dimension: WMeasurementDimension.Weight, scale: 0.000000000001}
        case WMeasurementUnits.Kilograms:  return {dimension: WMeasurementDimension.Weight, scale: 1000}

        // time
        case WMeasurementUnits.Seconds: return {dimension: WMeasurementDimension.Time, scale: 1 / (24 * 3600)}
        case WMeasurementUnits.Hours:   return {dimension: WMeasurementDimension.Time, scale: 1 / 24}
        case WMeasurementUnits.Days:    return {dimension: WMeasurementDimension.Time, scale: 1}
        case WMeasurementUnits.Weeks:   return {dimension: WMeasurementDimension.Time, scale: 7}
        case WMeasurementUnits.Months:  return {dimension: WMeasurementDimension.Time, scale: 30}
        case WMeasurementUnits.Years:   return {dimension: WMeasurementDimension.Time, scale: 365}

        // pure units
        case WMeasurementUnits.NA:              return {dimension: WMeasurementDimension.Pure, scale: 1}
        case WMeasurementUnits.Units:           return {dimension: WMeasurementDimension.Unit, scale: 1}
        case WMeasurementUnits.MilliUnits:      return {dimension: WMeasurementDimension.Unit, scale: 0.001}
        case WMeasurementUnits.Milliequivalent: return {dimension: WMeasurementDimension.Unit, scale: 0.001}

        case WMeasurementUnits.Mole:      return {dimension: WMeasurementDimension.Mole, scale: 1}
        case WMeasurementUnits.MilliMole: return {dimension: WMeasurementDimension.Mole, scale: 0.001}
        case WMeasurementUnits.NanoMole:  return {dimension: WMeasurementDimension.Mole, scale: 0.000000001}
        case WMeasurementUnits.PicoMole:  return {dimension: WMeasurementDimension.Mole, scale: 0.000000000001}

        case WMeasurementUnits.Percent: return {dimension: WMeasurementDimension.Pure, scale: 1}

        case WMeasurementUnits.MmHg: return {dimension: WMeasurementDimension.Pressure, scale: 1}
    }
}
 */
function maybeNull<T>(array: T[]|null): T[] {
    return array? array: [];
}
function length(array: any[]|null) {
    return maybeNull(array).length;
}
